import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ImagePlus, X, Loader2 } from "lucide-react";
import { API, authHeaders, formatErr, newId, compressImage } from "./api";
import { inputCls, Field, SaveBar, TabCard, Toggle, SortableList, SortableItem, DeleteButton } from "./fields";

const LABELS = ["Client", "Project partner", "Pilot", "Pre-launch", "Concept"];

const blankWork = (order) => ({
  id: newId(), name: "", label: "Client", summary: "", bullets: [], image: "", link: "", show_home: false, visible: true, order,
});

const ImageField = ({ item, token, onUploaded, onUnauthorized }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImage(file);
      const { data } = await axios.post(`${API}/media`, { data_url: dataUrl }, authHeaders(token));
      onUploaded(data.url);
      toast.success("Image uploaded (compressed under 300 KB).");
    } catch (err) {
      if (err.response?.status === 401) return onUnauthorized();
      toast.error(err.message || formatErr(err, "Image upload failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label="Image or logo" hint="Auto-compressed to under 300 KB. Empty = default icon.">
      <div className="flex items-center gap-4">
        {item.image ? (
          <span className="relative inline-block">
            <img src={item.image} alt="" className="h-14 w-auto border border-line bg-surface object-contain p-1.5" data-testid={`work-image-preview-${item.id}`} />
            <button type="button" onClick={() => onUploaded("")} data-testid={`work-image-remove-${item.id}`} aria-label="Remove image"
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500/90 text-white">
              <X size={11} />
            </button>
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`work-image-upload-${item.id}`}
          className="flex items-center gap-2 border border-dashed border-line px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
          {item.image ? "Replace" : "Upload"}
        </button>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={pick} />
      </div>
    </Field>
  );
};

export default function WorkTab({ content, token, onSaved, onUnauthorized }) {
  const [items, setItems] = useState(content.work);
  const [saving, setSaving] = useState(false);
  useEffect(() => setItems(content.work), [content.work]);

  const update = (id, patch) => setItems(items.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  const remove = (id) => setItems(items.filter((w) => w.id !== id));

  const save = async () => {
    for (const w of items) {
      if (!String(w.name).trim()) return toast.error("Every work item needs a name.");
    }
    const ordered = items.map((w, i) => ({ ...w, bullets: w.bullets.filter((b) => b.trim()), order: i }));
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/content/work`, { work: ordered }, authHeaders(token));
      onSaved(data);
      toast.success("Saved — live on the website now.");
    } catch (err) {
      if (err.response?.status === 401) return onUnauthorized();
      toast.error(formatErr(err, "Save failed. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TabCard title="Work / Clients" sub="Shown on /work — tick 'Homepage' to also feature on the home page" testid="tab-work">
      <p className="mb-4 text-xs uppercase tracking-[0.2em] text-faint">Drag the handle to reorder</p>
      <SortableList items={items} onReorder={setItems} testid="work-list">
        {(w) => (
          <SortableItem key={w.id} item={w} testid={`work-edit-${w.id}`}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name"><input value={w.name} onChange={(e) => update(w.id, { name: e.target.value })} data-testid={`work-name-${w.id}`} className={inputCls} /></Field>
              <Field label="Label">
                <select value={w.label} onChange={(e) => update(w.id, { label: e.target.value })} data-testid={`work-label-${w.id}`} className={inputCls}>
                  {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="One-line summary"><input value={w.summary} onChange={(e) => update(w.id, { summary: e.target.value })} data-testid={`work-summary-${w.id}`} className={inputCls} /></Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="What we did" hint="One bullet per line — optional">
                  <textarea rows={3} value={w.bullets.join("\n")} onChange={(e) => update(w.id, { bullets: e.target.value.split("\n") })} data-testid={`work-bullets-${w.id}`} className={`${inputCls} resize-none`} />
                </Field>
              </div>
              <ImageField item={w} token={token} onUploaded={(url) => update(w.id, { image: url })} onUnauthorized={onUnauthorized} />
              <Field label="Link" hint="Optional — e.g. https://client-site.com">
                <input value={w.link} onChange={(e) => update(w.id, { link: e.target.value })} data-testid={`work-link-${w.id}`} className={inputCls} />
              </Field>
              <div className="flex items-end justify-between gap-4 pb-1 sm:col-span-2">
                <div className="flex gap-6">
                  <label className="flex items-center gap-2.5 text-xs uppercase tracking-[0.15em] text-mut">
                    <Toggle checked={w.visible} onChange={(v) => update(w.id, { visible: v })} testid={`work-visible-${w.id}`} label="Visible" /> Show
                  </label>
                  <label className="flex items-center gap-2.5 text-xs uppercase tracking-[0.15em] text-mut">
                    <Toggle checked={w.show_home} onChange={(v) => update(w.id, { show_home: v })} testid={`work-home-${w.id}`} label="Show on homepage" /> Homepage
                  </label>
                </div>
                <DeleteButton onClick={() => remove(w.id)} testid={`work-delete-${w.id}`} />
              </div>
            </div>
          </SortableItem>
        )}
      </SortableList>
      <button
        type="button"
        onClick={() => setItems([...items, blankWork(items.length)])}
        data-testid="work-add"
        className="mt-4 flex items-center gap-2 border border-dashed border-line px-5 py-3 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop"
      >
        <Plus size={14} /> Add Work Item
      </button>
      <SaveBar onSave={save} saving={saving} lastSaved={content.saved_at?.work} testid="work-save" />
    </TabCard>
  );
}
